import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon, 
  ArrowRightIcon,
  ChartBarIcon,
  LightBulbIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ClockIcon,
  AcademicCapIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import FAQSection from '../components/FAQSection';

const ContentOptimizationPage = () => {
  const [content, setContent] = useState('');
  const [url, setUrl] = useState('');
  const [targetKeyword, setTargetKeyword] = useState('');
  const [analysisType, setAnalysisType] = useState('content'); // content, url, optimize
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('analysis');

  // Auto-load saved content analysis results on page mount
  useEffect(() => {
    const loadSavedResults = async () => {
      try {
        const response = await axios.get('/api/ai-content-analyzer/saved-results');
        if (response.data.success) {
          setResults(response.data);
          console.log('Loaded saved content analysis from', response.data.lastAnalyzed);
        }
      } catch (err) {
        console.log('No saved content results to load');
      }
    };
    loadSavedResults();
  }, []);


  const handleAnalyze = async () => {
    if (analysisType === 'content' && !content.trim()) {
      toast.error('Please enter content to analyze');
      return;
    }
    
    if (analysisType === 'url' && !url.trim()) {
      toast.error('Please enter a URL to analyze');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...(analysisType === 'content' ? { content: content.trim() } : { url: url.trim() }),
        target_keyword: targetKeyword.trim() || undefined
      };

      const response = await axios.post('/api/ai-content-analyzer/analyze', payload);

      if (response.data.success) {
        setResults(response.data);
        toast.success('Content analysis completed successfully');
      } else {
        toast.error('Analysis failed');
      }
    } catch (error) {
      console.error('Content analysis error:', error);
      toast.error(error.response?.data?.message || 'Failed to analyze content');
    } finally {
      setLoading(false);
    }
  };

  const handleOptimize = async () => {
    if (!content.trim()) {
      toast.error('Please enter content to optimize');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/ai-content-analyzer/optimize', {
        content: content.trim(),
        target_keyword: targetKeyword.trim() || undefined,
        content_type: 'article'
      });

      if (response.data.success) {
        setResults({
          ...response.data,
          optimization: true
        });
        setActiveTab('optimization');
        toast.success('Content optimization completed');
      } else {
        toast.error('Optimization failed');
      }
    } catch (error) {
      console.error('Content optimization error:', error);
      toast.error(error.response?.data?.message || 'Failed to optimize content');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };


  const CONTENT_OPT_FAQ = [
    { q: "What is content optimization and how does it improve rankings?", a: "Content optimization is the process of improving web content to maximize its search engine visibility, user engagement, and conversion potential. It involves optimizing keyword usage, improving readability, enhancing semantic relevance, adding structured data, optimizing meta tags, improving internal linking, and formatting content for both traditional search and AI-powered answer engines. Well-optimized content ranks higher and attracts more qualified traffic." },
    { q: "How does SERPfinity analyze and score content?", a: "SERPfinity uses AI-powered analysis to score your content across multiple dimensions including keyword optimization, semantic completeness, readability level, heading structure, internal and external linking, image optimization, schema markup, AEO readiness, and competitive content gap analysis. Each factor is weighted based on its ranking impact and you receive prioritized recommendations for improvement." },
    { q: "What is semantic SEO and why does it matter?", a: "Semantic SEO focuses on the meaning and context behind search queries rather than just matching keywords. It involves covering topics comprehensively, using related terms and entities, building topical authority, and structuring content to answer user intent fully. Search engines and AI platforms increasingly understand content semantically, so optimizing for meaning helps your content rank for broader query sets and get cited by AI assistants." },
    { q: "How should I optimize content for AI answer engines?", a: "To optimize for AI answer engines like ChatGPT and Google AI Overviews, structure content with clear question-and-answer formats, use comprehensive FAQ sections, implement structured data markup, build topical authority through content clusters, cite credible sources, maintain factual accuracy, and create authoritative expert-level content. SERPfinity scores your content AEO readiness and provides specific improvement steps." },
    { q: "What is the ideal content length for SEO?", a: "There is no universal ideal content length since it depends on the topic complexity and search intent. However, comprehensive content that thoroughly covers a topic tends to rank better. SERPfinity analyzes top-ranking content for your target keywords and recommends optimal length based on competitive benchmarks. We also identify content depth gaps where adding more detail on specific subtopics could improve rankings." },
    { q: "How often should I update existing content?", a: "Content freshness is a ranking factor, especially for topics where information changes frequently. We recommend auditing and updating high-priority content quarterly, refreshing statistics and data annually, and immediately updating content when significant industry changes occur. SERPfinity tracks content performance over time and alerts you when pages need updating to maintain or improve their rankings." }
  ];

  return (
    <>
        <Helmet>
          <title>Content Optimization Tool | SEO & AEO Content Analyzer | SERPfinity</title>
          <meta name="description" content="Optimize your content for search engines and AI platforms. Analyze readability, keyword usage, semantic relevance, and AEO signals with SERPfinity content optimization tool." />
          <meta name="keywords" content="content optimization tool, SEO content analyzer, AEO content optimization, readability checker, keyword density, semantic SEO, content scoring, AI content optimization, SERPfinity" />
          <link rel="canonical" href="https://thedigitalhall.club/content-optimization" />
          <meta property="og:title" content="Content Optimization Tool | SERPfinity" />
          <meta property="og:description" content="Optimize content for SEO and AI search platforms. Analyze readability, keywords, semantic relevance, and AEO signals." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://thedigitalhall.club/content-optimization" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Content Optimization Tool | SERPfinity" />
          <meta name="twitter:description" content="AI-powered content optimization for SEO and AEO. Improve rankings with data-driven content recommendations." />
          <meta name="robots" content="index, follow" />
          <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":CONTENT_OPT_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
        </Helmet>
      <Helmet>
        <title>AI Content Optimization - GPT-4 Powered Content Analysis | SERPfinity</title>
        <meta name="description" content="Optimize your content for AI search engines using GPT-4 powered suggestions. Get real-time content recommendations for better AI visibility." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <section className="bg-gradient-to-r from-green-600 to-emerald-700 text-white py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <DocumentTextIcon className="w-5 h-5 text-yellow-300" />
                <span className="text-sm font-medium">AI-Powered Optimization</span>
              </div>
              
              <h1 className="text-5xl font-bold mb-6">
                AI Content Optimizer
              </h1>
              
              <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                Generate and optimize content specifically for AI search algorithms. 
                Get GPT-4 powered suggestions to improve your content's AI visibility.
              </p>

              <div className="max-w-4xl mx-auto">
                {/* Analysis Type Selection */}
                <div className="flex justify-center gap-4 mb-6">
                  <button
                    onClick={() => setAnalysisType('content')}
                    className={`px-6 py-2 rounded-full font-medium transition-all ${
                      analysisType === 'content'
                        ? 'bg-yellow-400 text-gray-900'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    Analyze Content
                  </button>
                  <button
                    onClick={() => setAnalysisType('url')}
                    className={`px-6 py-2 rounded-full font-medium transition-all ${
                      analysisType === 'url'
                        ? 'bg-yellow-400 text-gray-900'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    Analyze URL
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {analysisType === 'content' ? (
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Paste your content here for AI analysis..."
                      className="lg:col-span-2 h-32 px-6 py-4 text-gray-900 rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none resize-none"
                    />
                  ) : (
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="Enter URL to analyze (e.g., https://example.com/page)"
                      className="lg:col-span-2 px-6 py-4 text-gray-900 rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                    />
                  )}
                  
                  <input
                    type="text"
                    value={targetKeyword}
                    onChange={(e) => setTargetKeyword(e.target.value)}
                    placeholder="Target keyword (optional)"
                    className="px-6 py-4 text-gray-900 rounded-lg border-0 focus:ring-2 focus:ring-yellow-400 focus:outline-none"
                  />
                </div>

                <div className="flex justify-center gap-4 mt-6">
                  <button
                    onClick={handleAnalyze}
                    disabled={loading}
                    className="px-8 py-4 bg-yellow-400 hover:bg-yellow-500 disabled:bg-yellow-300 text-gray-900 font-bold rounded-lg transition-all transform hover:scale-105 disabled:transform-none"
                  >
                    {loading ? 'Analyzing...' : 'Analyze Content'}
                  </button>
                  
                  {analysisType === 'content' && content.trim() && (
                    <button
                      onClick={handleOptimize}
                      disabled={loading}
                      className="px-8 py-4 bg-white/20 hover:bg-white/30 disabled:bg-white/10 text-white font-bold rounded-lg transition-all"
                    >
                      {loading ? 'Optimizing...' : 'Get Optimization'}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {results && (
          <section className="py-12">
            <div className="container mx-auto px-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Overall Score</p>
                      <p className={`text-2xl font-bold ${getScoreColor(results.analysis?.overall_score || 0)}`}>
                        {results.analysis?.overall_score || results.ai_score?.overall || 'N/A'}
                      </p>
                    </div>
                    <ChartBarIcon className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Word Count</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {results.analysis?.basic_metrics?.word_count || results.content_length || 0}
                      </p>
                    </div>
                    <DocumentTextIcon className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Reading Time</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {results.analysis?.basic_metrics?.reading_time || Math.ceil((results.content_length || 0) / 1000)} min
                      </p>
                    </div>
                    <ClockIcon className="w-8 h-8 text-purple-600" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Readability</p>
                      <p className={`text-2xl font-bold ${getScoreColor(results.analysis?.readability?.score || 0)}`}>
                        {results.analysis?.readability?.score || 'N/A'}
                      </p>
                    </div>
                    <AcademicCapIcon className="w-8 h-8 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="bg-white rounded-lg shadow-lg mb-6">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex">
                    <button
                      onClick={() => setActiveTab('analysis')}
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'analysis'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Analysis Results
                    </button>
                    <button
                      onClick={() => setActiveTab('recommendations')}
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'recommendations'
                          ? 'border-green-500 text-green-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Recommendations
                    </button>
                    {results.optimization && (
                      <button
                        onClick={() => setActiveTab('optimization')}
                        className={`py-4 px-6 border-b-2 font-medium text-sm ${
                          activeTab === 'optimization'
                            ? 'border-green-500 text-green-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        Optimization
                      </button>
                    )}
                  </nav>
                </div>

                <div className="p-6">
                  {activeTab === 'analysis' && results.analysis && (
                    <div className="space-y-8">
                      {/* Basic Metrics */}
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Content Metrics</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="text-sm text-gray-600">Word Count</div>
                            <div className="text-xl font-bold text-gray-900">{results.analysis.basic_metrics.word_count}</div>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="text-sm text-gray-600">Sentences</div>
                            <div className="text-xl font-bold text-gray-900">{results.analysis.basic_metrics.sentence_count}</div>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="text-sm text-gray-600">Paragraphs</div>
                            <div className="text-xl font-bold text-gray-900">{results.analysis.basic_metrics.paragraph_count}</div>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <div className="text-sm text-gray-600">Avg Sentence Length</div>
                            <div className="text-xl font-bold text-gray-900">{results.analysis.basic_metrics.avg_sentence_length}</div>
                          </div>
                        </div>
                      </div>

                      {/* Readability */}
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Readability Analysis</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className={`p-6 rounded-lg ${getScoreBgColor(results.analysis.readability.score)}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <AcademicCapIcon className="w-5 h-5" />
                              <span className="font-medium">Readability Score</span>
                            </div>
                            <div className={`text-3xl font-bold ${getScoreColor(results.analysis.readability.score)}`}>
                              {results.analysis.readability.score}/100
                            </div>
                          </div>
                          <div className="p-6 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                              <span className="font-medium text-blue-900">Grade Level</span>
                            </div>
                            <div className="text-2xl font-bold text-blue-600">
                              Grade {results.analysis.readability.grade_level}
                            </div>
                          </div>
                          <div className="p-6 bg-purple-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <ChartBarIcon className="w-5 h-5 text-purple-600" />
                              <span className="font-medium text-purple-900">Avg Words/Sentence</span>
                            </div>
                            <div className="text-2xl font-bold text-purple-600">
                              {results.analysis.readability.avg_words_per_sentence}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AI Optimization */}
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-4">AI Optimization Analysis</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <div className="text-sm text-green-600 font-medium mb-1">Questions</div>
                            <div className="text-2xl font-bold text-green-600">{results.analysis.ai_optimization.question_count}</div>
                          </div>
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="text-sm text-blue-600 font-medium mb-1">Definitions</div>
                            <div className="text-2xl font-bold text-blue-600">{results.analysis.ai_optimization.definition_phrases}</div>
                          </div>
                          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                            <div className="text-sm text-purple-600 font-medium mb-1">Examples</div>
                            <div className="text-2xl font-bold text-purple-600">{results.analysis.ai_optimization.example_count}</div>
                          </div>
                          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="text-sm text-orange-600 font-medium mb-1">Conversational %</div>
                            <div className="text-2xl font-bold text-orange-600">{results.analysis.ai_optimization.conversational_percentage}%</div>
                          </div>
                        </div>
                        
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            {results.analysis.ai_optimization.ai_friendly ? (
                              <CheckCircleIcon className="w-5 h-5 text-green-600" />
                            ) : (
                              <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                            )}
                            <span className="font-medium text-gray-900">
                              {results.analysis.ai_optimization.ai_friendly ? 'AI-Friendly Content' : 'Needs AI Optimization'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {results.analysis.ai_optimization.ai_friendly 
                              ? 'Your content follows AI-friendly patterns with good question usage and conversational tone.'
                              : 'Consider adding more questions, definitions, and conversational elements to improve AI readability.'}
                          </p>
                        </div>
                      </div>

                      {/* Keyword Analysis */}
                      {results.analysis.keyword_analysis && (
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-4">Keyword Analysis</h3>
                          <div className="p-6 border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-lg font-medium text-gray-900">
                                Target Keyword: "{results.analysis.keyword_analysis.keyword}"
                              </h4>
                              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                                results.analysis.keyword_analysis.optimal_density 
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {results.analysis.keyword_analysis.optimal_density ? 'Optimal' : 'Needs Work'}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                              <div>
                                <div className="text-sm text-gray-600">Exact Matches</div>
                                <div className="text-xl font-bold text-gray-900">{results.analysis.keyword_analysis.exact_matches}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Density</div>
                                <div className="text-xl font-bold text-gray-900">{results.analysis.keyword_analysis.density}%</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">In Title</div>
                                <div className="text-xl font-bold text-gray-900">
                                  {results.analysis.keyword_analysis.in_title ? 'Yes' : 'No'}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">In First Paragraph</div>
                                <div className="text-xl font-bold text-gray-900">
                                  {results.analysis.keyword_analysis.in_first_paragraph ? 'Yes' : 'No'}
                                </div>
                              </div>
                            </div>
                            
                            {results.analysis.keyword_analysis.recommendations.length > 0 && (
                              <div>
                                <h5 className="font-medium text-gray-900 mb-2">Keyword Recommendations:</h5>
                                <ul className="space-y-1">
                                  {results.analysis.keyword_analysis.recommendations.map((rec, index) => (
                                    <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                                      <LightBulbIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                      {rec}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'recommendations' && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-6">AI Content Recommendations</h3>
                      
                      {results.analysis && (
                        <div className="space-y-6">
                          {/* Readability Recommendations */}
                          {results.analysis.readability.recommendations.length > 0 && (
                            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
                              <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                                <AcademicCapIcon className="w-5 h-5" />
                                Readability Improvements
                              </h4>
                              <ul className="space-y-2">
                                {results.analysis.readability.recommendations.map((rec, index) => (
                                  <li key={index} className="text-blue-800 flex items-start gap-2">
                                    <LightBulbIcon className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Structure Recommendations */}
                          {results.analysis.structure.recommendations.length > 0 && (
                            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                              <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                                <DocumentTextIcon className="w-5 h-5" />
                                Content Structure
                              </h4>
                              <ul className="space-y-2">
                                {results.analysis.structure.recommendations.map((rec, index) => (
                                  <li key={index} className="text-green-800 flex items-start gap-2">
                                    <LightBulbIcon className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* AI Optimization Recommendations */}
                          {results.analysis.ai_optimization.recommendations.length > 0 && (
                            <div className="p-6 bg-purple-50 border border-purple-200 rounded-lg">
                              <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                                <SparklesIcon className="w-5 h-5" />
                                AI Optimization
                              </h4>
                              <ul className="space-y-2">
                                {results.analysis.ai_optimization.recommendations.map((rec, index) => (
                                  <li key={index} className="text-purple-800 flex items-start gap-2">
                                    <LightBulbIcon className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Generic AI Optimization Tips */}
                      <div className="mt-8">
                        <h4 className="text-lg font-bold text-gray-900 mb-4">General AI Content Tips</h4>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <h5 className="font-bold text-gray-900 mb-2">Question-Answer Format</h5>
                            <p className="text-sm text-gray-600">Structure content with clear questions and direct answers to match AI search patterns.</p>
                          </div>
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <h5 className="font-bold text-gray-900 mb-2">Conversational Tone</h5>
                            <p className="text-sm text-gray-600">Use "you", "your", and natural language to align with voice search queries.</p>
                          </div>
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <h5 className="font-bold text-gray-900 mb-2">Clear Definitions</h5>
                            <p className="text-sm text-gray-600">Define key terms and concepts clearly for better AI understanding.</p>
                          </div>
                          <div className="p-4 bg-white border border-gray-200 rounded-lg">
                            <h5 className="font-bold text-gray-900 mb-2">Structured Data</h5>
                            <p className="text-sm text-gray-600">Use schema markup to help AI engines understand your content context.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'optimization' && results.optimization && (
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-6">Content Optimization Results</h3>
                      
                      {/* Score Comparison */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div className="p-6 bg-gray-50 rounded-lg">
                          <h4 className="font-bold text-gray-900 mb-2">Original Score</h4>
                          <div className={`text-3xl font-bold ${getScoreColor(results.original_score)}`}>
                            {results.original_score}/100
                          </div>
                        </div>
                        <div className="p-6 bg-green-50 rounded-lg">
                          <h4 className="font-bold text-gray-900 mb-2">Optimized AI Score</h4>
                          <div className={`text-3xl font-bold ${getScoreColor(results.ai_score.overall)}`}>
                            {results.ai_score.overall}/100
                          </div>
                        </div>
                      </div>
                      
                      {/* Improvements */}
                      {results.improvements && results.improvements.length > 0 && (
                        <div className="mb-8">
                          <h4 className="text-lg font-bold text-gray-900 mb-4">Recommended Improvements</h4>
                          <div className="space-y-4">
                            {results.improvements.map((improvement, index) => (
                              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-bold text-gray-900">{improvement.title}</h5>
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      improvement.priority === 'high' ? 'bg-red-100 text-red-800' :
                                      improvement.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-blue-100 text-blue-800'
                                    }`}>
                                      {improvement.priority} priority
                                    </span>
                                    <span className="text-xs text-gray-500">{improvement.impact} impact</span>
                                  </div>
                                </div>
                                <p className="text-gray-600">{improvement.description}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* AI Score Breakdown */}
                      {results.ai_score && results.ai_score.breakdown && (
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-4">AI Score Breakdown</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {Object.entries(results.ai_score.breakdown).map(([category, score]) => (
                              <div key={category} className={`p-4 rounded-lg ${getScoreBgColor(score)}`}>
                                <div className="text-sm font-medium text-gray-600 mb-1 capitalize">
                                  {category.replace('_', ' ')}
                                </div>
                                <div className={`text-xl font-bold ${getScoreColor(score)}`}>
                                  {score}/100
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {!results && (
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  AI-First Content Strategy
                </h2>
                <p className="text-xl text-gray-600 mb-12">
                  Our AI Content Optimizer helps you create content that ranks in both traditional and AI search
                </p>
                
                <div className="grid md:grid-cols-2 gap-8 text-left">
                  {[
                    {
                      title: 'Content Structure Analysis',
                      description: 'Optimize headings, paragraphs, and lists for maximum AI readability'
                    },
                    {
                      title: 'Entity Optimization',
                      description: 'Strengthen entity associations for better Knowledge Graph presence'
                    },
                    {
                      title: 'Question Targeting',
                      description: 'Identify and answer questions that AI engines frequently surface'
                    },
                    {
                      title: 'Schema Generation',
                      description: 'Auto-generate optimal schema markup for your content type'
                    },
                    {
                      title: 'Snippet Optimization',
                      description: 'Format content to maximize featured snippet opportunities'
                    },
                    {
                      title: 'AI Readability Score',
                      description: 'Get a comprehensive score on how AI-friendly your content is'
                    }
                  ].map((feature) => (
                    <div key={feature.title} className="bg-white rounded-lg shadow-lg p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
        <FAQSection items={CONTENT_OPT_FAQ} />
    </>
  );
};

export default ContentOptimizationPage;
